import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { SUPPORTED_LANGUAGES, SupportedLanguage } from "@/config/config";

/**
 * Hook for switching languages while preserving the current route and search parameters
 *
 * @example
 * ```tsx
 * const { switchLanguage, currentLanguage } = useLanguageSwitcher();
 *
 * // Switch to Arabic
 * switchLanguage('ar');
 *
 * // Check current language
 * console.log(currentLanguage); // 'en' or 'ar'
 * ```
 */
export const useLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const routerState = useRouterState();
  const navigate = useNavigate();

  // Get current language from the route
  const currentLanguage: SupportedLanguage = (() => {
    const langFromPath = routerState.location.pathname.split("/")[1] as SupportedLanguage;
    return SUPPORTED_LANGUAGES.includes(langFromPath) ? langFromPath : "en";
  })();

  const switchLanguage = useCallback(
    (newLang: SupportedLanguage) => {
      // Don't switch if it's the same language
      if (newLang === currentLanguage) {
        return;
      }

      // Update i18n state and localStorage
      i18n.changeLanguage(newLang);
      localStorage.setItem("lang", newLang);

      // Extract the current path without language prefix
      const currentPath = routerState.location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, "") || "/";

      // Build new path with the new language
      const newPath = `/${newLang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;

      // Preserve search parameters
      const searchQuery = routerState.location.search;

      // Navigate to the new language-specific URL
      navigate({
        to: newPath,
        search: searchQuery,
        replace: true, // Replace history entry to avoid clutter
      });
    },
    [currentLanguage, i18n, routerState.location.pathname, routerState.location.search],
  );

  return {
    switchLanguage,
    currentLanguage,
    availableLanguages: SUPPORTED_LANGUAGES,
  };
};
